import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../../api/queryClient';
import { getV1BusinessPartnerCurrentRequestChangeOwnership } from '../../api/generated/pm';
import {
  getV1BusinessPartnerCurrentRequestChangeOwnershipQueryKey,
  getV1RequestChangeOwnershipByIdOptions,
  getV1RequestChangeOwnershipByIdQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import { getErrorStatus } from '../../utils/errorStatus';

/**
 * Query hooks for Change Ownership: the current Business Partner's pending
 * request (read on Settings → General Information) and a request read by id
 * from the emailed accept link. See
 * `../Settings/IdentityProviders/identityProviders.queries.ts` for the pattern.
 */

/** Resolves to `null` when no request is pending (the API answers 404). */
export function useCurrentChangeOwnershipRequest() {
  return useQuery({
    queryKey: getV1BusinessPartnerCurrentRequestChangeOwnershipQueryKey(),
    queryFn: async ({ signal }) => {
      try {
        const { data } = await getV1BusinessPartnerCurrentRequestChangeOwnership({
          signal,
          throwOnError: true,
        });
        return data;
      } catch (error) {
        if (getErrorStatus(error) === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

export const invalidateCurrentChangeOwnershipRequest = () =>
  queryClient.invalidateQueries({
    queryKey: getV1BusinessPartnerCurrentRequestChangeOwnershipQueryKey(),
  });

export function useRequestChangeOwnership(id: string | null) {
  return useQuery({
    ...getV1RequestChangeOwnershipByIdOptions({ path: { id: id as string } }),
    enabled: !!id,
  });
}

/**
 * Clears every cached change-ownership request regardless of id (the generated
 * key requires a path, so the prefix is derived by dropping it).
 */
export const invalidateRequestChangeOwnership = () => {
  const [{ _id, baseUrl }] = getV1RequestChangeOwnershipByIdQueryKey({ path: { id: '' } });
  return queryClient.invalidateQueries({ queryKey: [{ _id, baseUrl }] });
};
